import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2Icon } from 'lucide-react';

import { getConfig } from '@gokrew/ui/runtime-config';
import { TranslationNS } from '@gokrew/models/constants';
import { useEmployeePortalAuth } from '@/context/EmployeeAuthContext';
import styles from './Sign.module.css';

const LANGUAGES = ['fr', 'en'];

/**
 * Where the signing page sends every signer back (spec 6.2, `redirectUrl`;
 * the engine has one per envelope). The URL carries the document language
 * (`lang`), `for=candidate` for an ATS offer (a candidate has no documents
 * here: their copy comes by email) and, when the company countersigns, the
 * request id (`request`), so the HR signer, who is not on this portal, gets a
 * link back to the request in the company portal (fresh-eyes QA-06).
 */
export function SignCompletePage() {
  const { t, i18n } = useTranslation(TranslationNS.SignatureRequests);
  const { token } = useEmployeePortalAuth();
  const [search] = useSearchParams();
  const candidate = search.get('for') === 'candidate';
  const requestId = search.get('request');
  const language = search.get('lang');
  const companyPortal = getConfig().COMPANY_PORTAL_URL?.replace(/\/+$/, '');
  const backToRequest =
    companyPortal && requestId && /^[0-9a-f-]{36}$/i.test(requestId)
      ? `${companyPortal}/esignatures/requests/${requestId}`
      : null;

  // A visitor who is not logged in here reads the document's language, not
  // the browser's; a logged-in employee keeps their own.
  useEffect(() => {
    if (
      !token &&
      language &&
      LANGUAGES.includes(language) &&
      !i18n.language.startsWith(language)
    ) {
      void i18n.changeLanguage(language);
    }
  }, [i18n, language, token]);

  return (
    <div className={styles.gate}>
      <CheckCircle2Icon className={styles.doneIcon} aria-hidden />
      <h2 className={styles.heading}>{t('complete.title')}</h2>
      <p className={styles.subtext}>
        {t(candidate ? 'complete.candidate_message' : 'complete.message')}
      </p>
      {token && !candidate && (
        <Link to="/signatures" className={styles.link}>
          {t('gate.my_documents')}
        </Link>
      )}
      {backToRequest && (
        <div className={styles.companyBox}>
          <p className={styles.subtext}>{t('complete.company_signer')}</p>
          <a href={backToRequest} className={styles.link}>
            {t('complete.back_to_request')}
          </a>
        </div>
      )}
    </div>
  );
}

export default SignCompletePage;
